import { Fragment } from 'react';
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbLink,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { useSidebar } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';

type Props = {
  title?: string;
  headerActions?: React.ReactNode;
  headerChildren?: React.ReactNode;
  className?: string;
  mainContentClassName?: string;
  noPaddingOnContent?: boolean;
  headerClassName?: string;
  breadcrumbItems?: {
    label: string;
    to?: string;
  }[];
};

const SidebarTrigger = () => {
  const { open, toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className="size-7 text-foreground bg-sidebar shrink-0"
      onClick={toggleSidebar}
    >
      {open ? <PanelLeftClose /> : <PanelLeftOpen />}
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
};

const Main = ({
  title,
  headerActions,
  headerChildren,
  children,
  noPaddingOnContent = false,
  mainContentClassName,
  headerClassName,
  className,
  breadcrumbItems = [
    {
      label: title ?? '',
    },
  ],
}: React.PropsWithChildren<Props>) => {
  const mainClasses = cn('flex flex-col grow min-h-0 min-w-0', className);
  const mainContentClasses = cn('overflow-y-auto grow', mainContentClassName, {
    'p-0': noPaddingOnContent,
    'p-6': !noPaddingOnContent,
  });

  return (
    <main className={mainClasses}>
      <div className={cn('shrink-0 bg-white border-b border-border relative', headerClassName)}>
        <div className="flex justify-between items-center h-15 sm:h-19 py-4 px-4 sm:px-6 gap-2">
          <div className="flex items-center gap-2 overflow-hidden">
            <SidebarTrigger />
            <div className="w-px h-[15px] bg-border rounded-full mr-2" />
            <Breadcrumb className="overflow-hidden">
              <BreadcrumbList className="flex-nowrap md:flex-wrap [&>li:last-child]:min-w-0 [&>li:last-child>span]:truncate [&>li:not(:last-child)]:shrink-0">
                {breadcrumbItems.map(({ label, to }, index) => (
                  <Fragment key={label}>
                    <BreadcrumbItem>
                      {to ? <BreadcrumbLink to={to}>{label}</BreadcrumbLink> : <BreadcrumbPage>{label}</BreadcrumbPage>}
                    </BreadcrumbItem>

                    {index < breadcrumbItems.length - 1 && (
                      <BreadcrumbSeparator className="[&>svg]:size-6 shrink-0 text-muted-foreground">
                        <ChevronRight />
                      </BreadcrumbSeparator>
                    )}
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {headerActions && <div className="shrink-0">{headerActions}</div>}
        </div>

        {headerChildren}
      </div>

      <div className={mainContentClasses}>{children}</div>
    </main>
  );
};

export default Main;
